import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';

import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SplitterModule } from 'primeng/splitter';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';

import { MailService } from '../../services/mail.service';
import { EmailListItem, EmailDetail, AttachmentInfo, DomainConfig } from '../../models/mail.model';
import { MailComposeComponent } from '../mail-compose/mail-compose.component';

@Component({
  selector: 'app-mail-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    SplitterModule,
    ToolbarModule,
    TooltipModule,
    DialogModule,
    ProgressSpinnerModule,
    TagModule,
    MessageModule,
    MailComposeComponent,
  ],
  templateUrl: './mail-view.component.html',
  styleUrl: './mail-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailViewComponent {
  recipientAddress = '';
  emails: EmailListItem[] = [];
  selectedEmail: EmailDetail | null = null;
  selectedEmailId: number | null = null;
  domainConfig: DomainConfig | null = null;

  loading = false;
  fetching = false;
  statusMessage = '';
  errorMessage = '';

  // PDF-Viewer
  showPdfViewer = false;
  pdfUrl: SafeResourceUrl | null = null;
  pdfFilename = '';

  // Compose-Dialog
  showCompose = false;
  composeMode: 'new' | 'reply' = 'new';
  replyToEmail: EmailDetail | null = null;

  @ViewChild('mailBody') mailBodyRef!: ElementRef;

  constructor(
    private mailService: MailService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.mailService.getDomainConfig().subscribe({
      next: (config) => {
        this.domainConfig = config;
        this.cdr.markForCheck();
      },
    });

    // E-Mail-Adresse aus URL-Parameter lesen und direkt laden
    this.route.queryParams.subscribe((params) => {
      const emailParam = params['email'];
      if (emailParam) {
        this.recipientAddress = emailParam.trim().toLowerCase();
        // Warten bis domainConfig geladen ist, dann Mails abrufen
        if (this.domainConfig) {
          this.onFetchMails();
        } else {
          this.mailService.getDomainConfig().subscribe({
            next: (config) => {
              this.domainConfig = config;
              this.cdr.markForCheck();
              this.onFetchMails();
            },
          });
        }
      }
    });
  }

  onFetchMails(): void {
    if (!this.recipientAddress) return;

    const addr = this.recipientAddress.trim().toLowerCase();
    if (this.domainConfig && !addr.endsWith(`@${this.domainConfig.domain}`)) {
      this.errorMessage = `Adresse muss auf @${this.domainConfig.domain} enden`;
      this.cdr.markForCheck();
      return;
    }
    if (this.domainConfig && addr === `mailhub@${this.domainConfig.domain}`) {
      this.errorMessage = 'Zugriff auf das Sammelpostfach ist nicht erlaubt';
      this.cdr.markForCheck();
      return;
    }

    this.errorMessage = '';
    this.fetching = true;
    this.statusMessage = 'Emails werden abgerufen...';
    this.cdr.markForCheck();

    this.mailService.fetchMails(addr).subscribe({
      next: (res) => {
        this.statusMessage = res.message;
        this.fetching = false;
        this.loadMailList();
      },
      error: (err) => {
        this.errorMessage = err.error?.detail || 'Fehler beim Abrufen';
        this.fetching = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadMailList(): void {
    if (!this.recipientAddress) return;

    this.loading = true;
    this.cdr.markForCheck();

    this.mailService.listMails(this.recipientAddress.trim().toLowerCase()).subscribe({
      next: (mails) => {
        this.emails = mails;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onSelectEmail(email: EmailListItem): void {
    this.selectedEmailId = email.id;
    this.cdr.markForCheck();

    this.mailService.getMail(email.id).subscribe({
      next: (detail) => {
        this.selectedEmail = detail;
        this.cdr.markForCheck();
      },
    });
  }

  getMailBody(): SafeHtml | null {
    if (!this.selectedEmail) return null;
    const content = this.selectedEmail.body_html || this.selectedEmail.body_text || '';
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getSenderDisplay(email: EmailListItem | EmailDetail): string {
    if (email.sender_name) {
      return `${email.sender_name} <${email.sender}>`;
    }
    return email.sender;
  }

  // Anhaenge
  isPdf(att: AttachmentInfo): boolean {
    return att.content_type === 'application/pdf';
  }

  openAttachment(att: AttachmentInfo): void {
    if (this.isPdf(att)) {
      this.pdfFilename = att.filename;
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.mailService.getAttachmentPreviewUrl(att.id)
      );
      this.showPdfViewer = true;
    } else {
      // Download
      window.open(this.mailService.getAttachmentUrl(att.id), '_blank');
    }
    this.cdr.markForCheck();
  }

  closePdfViewer(): void {
    this.showPdfViewer = false;
    this.pdfUrl = null;
    this.cdr.markForCheck();
  }

  // Compose
  openNewMail(): void {
    this.composeMode = 'new';
    this.replyToEmail = null;
    this.showCompose = true;
    this.cdr.markForCheck();
  }

  openReply(): void {
    if (!this.selectedEmail) return;
    this.composeMode = 'reply';
    this.replyToEmail = this.selectedEmail;
    this.showCompose = true;
    this.cdr.markForCheck();
  }

  onComposeClosed(): void {
    this.showCompose = false;
    this.cdr.markForCheck();
  }

  onMailSent(): void {
    this.showCompose = false;
    this.statusMessage = 'Email wurde gesendet';
    this.cdr.markForCheck();
  }

  onKeydownRecipient(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onFetchMails();
    }
  }
}
